import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries";
import { useValidator } from "@common/hooks/validation";
import { ValidationError } from "@common/validationError";
import { GetIncidentLogPlanLimit } from "../../auth/planProvider/PlanProvider.hooks";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { EntityTemplate, IncidentLogStandardField, TemplateEntityType } from "../../entityTemplates/EntityTemplate.types";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { IncidentLogFieldModel } from "../../entityTemplates/IncidentLogFieldRenderers";
import { groupFieldsByTemplateStep } from "../../entityTemplates/templateStepFields/groupFieldsByTemplateStep";
import { getIncidentLogFieldName, getIncidentLogFieldValue, IncidentLogRelatedValues } from "../editIncidentLog/EditIncidentLogValidator.hooks";
import { useUrlProvider } from "../../useUrlProvider";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { useEntityTemplateLockedText } from "../../entityTemplates/useEntityTemplateLockedText";
import { AddIncidentLogPaneProps, AddIncidentLogSaveModel, CreateIncidentLogModel } from "./AddIncidentLogPane.types";

// Relations, documents and excluded users are saved against the incident's id, and creation defaults the
// responsible and group companies. The template editor no longer lets them be required at creation, but a
// template that marked them before that still says they are, so the pane skips them.
const fieldsSetAfterCreation: ReadonlySet<number> = new Set([
    IncidentLogStandardField.IncidentLogRelations,
    IncidentLogStandardField.IncidentLogDocuments,
    IncidentLogStandardField.IncidentLogExcludedUsers,
    IncidentLogStandardField.IncidentLogResponsible,
    IncidentLogStandardField.IncidentLogGroupCompanies,
]);

function createEmptyRequiredFieldValues(description: string | undefined): IncidentLogFieldModel {
    return {
        id: "",
        description: description,
        types: [],
        businessAreaIds: [],
        complianceAreaIds: [],
        isStartUnknown: false,
        isEndDateOngoing: false,
        isEstimatedNumberOfAffectedPeopleUnknown: false,
        isEstimatedNumberOfAffectedRecordsUnknown: false,
        requestedDataCategoryIds: [],
        dataCategoryIds: [],
        dataSubjectIds: [],
        affectedServiceIds: [],
        affectedSecurityProperties: [],
        affectedCountryCodes: [],
        customFieldValues: [],
        excludedUsers: [],
        additionalAccessUsers: [],
        visibleToAllUsers: false,
        accessBusinessAreas: [],
        allGroupEntityAccess: true,
        groupEntities: [],
    };
}

export function useAddIncidentLogPane(props: AddIncidentLogPaneProps) {
    const { translateString } = useTranslation();
    const { getLockedTextsById } = useEntityTemplateLockedText();
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const { getIncidentLogEditUrl } = useUrlProvider();
    const { close, setHasUnsavedChanges } = usePane();
    const { platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const incidentLogPlanLimit = GetIncidentLogPlanLimit();

    const [saveModel, setSaveModel] = useState<AddIncidentLogSaveModel>({
        name: props.observation?.name ?? "",
        entityTemplateId: "",
    });
    // The observation's description is what happened, so it carries over whether or not the template asks for it
    const [editedIncidentLog, setEditedIncidentLog] = useState<IncidentLogFieldModel>(createEmptyRequiredFieldValues(props.observation?.description));
    const [selectedTagIds, setSelectedTagIds] = useState<Array<string>>([]);
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.IncidentLog, false);
    const templates = (entityTemplatesData ?? []).map((x) => ({ id: x.id, name: x.name, disabled: !x.hasAccess }));
    const lockedTemplateTexts = getLockedTextsById(entityTemplatesData ?? []);

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === saveModel.entityTemplateId);
    // The full template carries no such flag: the selectable view model is what reports that the user is
    // barred from a field this template insists on at creation time.
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const templateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${saveModel.entityTemplateId}`, { enabled: !!saveModel.entityTemplateId });
    const template = templateQuery.data;

    const tagsAvailable = platformFeatures.tags && permissions.canAccessTags;

    const requiredAtCreationFields = (template?.steps.flatMap((step) => step.fields) ?? []).filter(
        (field) =>
            field.requiredAtCreation &&
            !(field.standardFieldIdentifier && fieldsSetAfterCreation.has(field.standardFieldIdentifier)) &&
            (field.standardFieldIdentifier !== IncidentLogStandardField.IncidentLogTags || tagsAvailable)
    );

    const fieldGroups = groupFieldsByTemplateStep(template, requiredAtCreationFields);

    const validation = useValidator<AddIncidentLogSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("incidentLogTemplate")),
        }),
        []
    );

    validation.check(saveModel);

    // Locked templates are listed but cannot be chosen, so the only unlocked one is as good as the only one
    useEffect(() => {
        const unlockedTemplates = entityTemplatesData?.filter((x) => x.hasAccess);
        if (unlockedTemplates?.length === 1 && !saveModel.entityTemplateId) {
            setSaveModel((current) => ({ ...current, entityTemplateId: unlockedTemplates[0].id }));
        }
    }, [entityTemplatesData, saveModel.entityTemplateId]);

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");
        const relatedValues: IncidentLogRelatedValues = { selectedTags: selectedTagIds, relationIds: [], documentIds: [] };

        for (const field of requiredAtCreationFields) {
            // The name is validated with the save model, where the pane keeps it
            if (field.standardFieldIdentifier === IncidentLogStandardField.IncidentLogName) {
                continue;
            }

            if (!hasFieldData(getIncidentLogFieldValue(field, editedIncidentLog, relatedValues))) {
                errors.push(new ValidationError(getIncidentLogFieldName(field), requiredTranslation));
            }
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const modelUpdater = {
        onNameChange: (name: string) => updateModel({ name: name }),
        onTemplateChange: (entityTemplateId: string | null) => {
            updateModel({ entityTemplateId: entityTemplateId ?? "" });
            setEditedIncidentLog(createEmptyRequiredFieldValues(props.observation?.description));
            setSelectedTagIds([]);
            setShowRequiredFieldErrors(false);
        },
        onTemplateFieldChange: async (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => {
            setHasUnsavedChanges(true);
            setEditedIncidentLog((current) => {
                const next = { ...current };
                changeFunc(next);
                return next;
            });
        },
        onTagsChange: (tagIds: Array<string>) => {
            setHasUnsavedChanges(true);
            setSelectedTagIds(tagIds);
        },
    };

    function updateModel(changes: Partial<AddIncidentLogSaveModel>) {
        setHasUnsavedChanges(true);
        setSaveModel((current) => ({ ...current, ...changes }));
    }

    function onSaveClick() {
        const requiredFieldErrors = validateRequiredAtCreationFields();

        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }

        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }

        if (validation.anyHasErrors || requiredFieldErrors.length > 0) {
            return;
        }

        createIncident();
    }

    const saveMutation = useDotLegalMutation(save);
    function createIncident() {
        saveMutation.mutateAsync(mapToCreateModel(), {
            onSuccess: (response) => {
                snackbar.show(translateString("incidentCreatedSnackbar"));
                // A pane outlives a navigation, so it has to close itself or it stays open over the new incident
                close();
                navigateToCreatedIncident(response.value().id);
            },
        });
    }

    // The steps open on a brand new incident, so they must not flag missing required fields before the user has
    // had a chance to reach them.
    function navigateToCreatedIncident(incidentLogId: string) {
        navigate(getIncidentLogEditUrl(incidentLogId, "create=true"));
    }

    return {
        saveModel,
        modelUpdater,
        onSaveClick,
        templates,
        templatesIsLoading,
        lockedTemplateTexts,
        isLoadingTemplate: templateQuery.isLoading && !!saveModel.entityTemplateId,
        selectedTemplateMissingAccess,
        fieldGroups,
        editedIncidentLog,
        selectedTagIds,
        requiredFieldValidationErrors,
        validation,
        isSaving: saveMutation.isLoading,
        showTrialLimitWarning: incidentLogPlanLimit?.data?.warnLimitReachedOnTrial ?? false,
    };

    function save(model: CreateIncidentLogModel) {
        if (props.observation) {
            return post<{ id: string }>(`/observations/${props.observation.id}/convert-to-incident`, model);
        }

        return post<{ id: string }>("/incident-log-entries", model);
    }

    function mapToCreateModel(): CreateIncidentLogModel {
        const { id, status, responsibleId, allGroupEntityAccess, excludedUsers, groupEntities, ...collectedFields } = editedIncidentLog;

        return {
            ...collectedFields,
            name: saveModel.name,
            entityTemplateId: saveModel.entityTemplateId,
            tagIds: selectedTagIds,
        };
    }
}
